import {
  isElement,
  removeNodes,
  setNodes,
  getPluginOptions,
} from '@udecode/plate-common';
import { getTableColumnCount, ELEMENT_TABLE } from '@udecode/plate-table';

import { KEY_DOCUMENT_SETTINGS } from '../document-settings/createDocumentSettingsPlugin';

/**
 * Normalize table:
 *  - remove children that aren't table rows.
 *  - adds default col sizes if it does not exist.
 *  - ensures that all col sizes are at least the min width and adjusts
 *    when all col sizes exceeds the max.
 *  - move table cell attributes colspan and rowspan to the element.
 */
export const withNormalizeTable2 = (editor) => {
  const { normalizeNode } = editor;

  editor.normalizeNode = ([node, path]) => {
    if (isElement(node)) {
      // Check for non TR elements within a table's children. Remove if exists.
      if (node.type === 'table') {
        const { children: rows, colSizes } = node;

        const found = rows.findIndex(row => row.type !== 'tr');
        if (found >= 0) {
          removeNodes(editor, { at: [...path, found] });
          return;
        };

        const { minColumnWidth = 48 } = getPluginOptions(editor, ELEMENT_TABLE);
        const { innerWidth = minColumnWidth } = getPluginOptions(editor, KEY_DOCUMENT_SETTINGS);

        if (!colSizes) {
          const maxColLength = getTableColumnCount(node);
          const width = Math.floor(innerWidth / maxColLength);
          const newColSizes = Array(maxColLength).fill(width);
          setNodes(editor, { colSizes: newColSizes }, { at: path });
          return;
        }

        // Fix colsizes if any are less than min col size.
        const newColSizes = [...colSizes];
        if (!newColSizes.some(value => value < minColumnWidth)) {
          // Dont need to adjust.
          return;
        }

        const adjustableIndices = [];

        for (let i = 0; i < newColSizes.length; i += 1) {
          if (newColSizes[i] < minColumnWidth) {
            newColSizes[i] = minColumnWidth;
          } else {
            adjustableIndices.push(i);
          }
        }

        const currentSum = newColSizes.reduce((sum, val) => sum + val, 0);
        if (currentSum <= innerWidth) {
          // no adjustment needed.
          return;
        }

        const excess = currentSum - innerWidth;
        if (adjustableIndices.length === 0) {
          throw new Error('Cannot satisfy constraints - no adjustable values');
        }

        const adjustableSum = adjustableIndices.reduce((sum, i) => sum + newColSizes[i], 0);
        let remaining = excess;
        for (let i = 0; i < adjustableIndices.length; i += 1) {
          const idx = adjustableIndices[i];
          // Last element gets the remainder to handle rounding.
          const reduction = i === adjustableIndices.length - 1 ?
            remaining : Math.round((newColSizes[i] / adjustableSum) * excess);

          newColSizes[idx] = Math.max(minColumnWidth, newColSizes[idx] - reduction);
          remaining -= reduction;
        }

        setNodes(editor, { colSizes: newColSizes }, { at: path });
      }

      if (node.type === 'td') {
        const { attributes, colSpan, rowSpan } = node;
        const { colspan, rowspan } = attributes || {};

        if (colspan && !colSpan) {
          setNodes(editor, { colSpan: parseInt(colspan, 10) }, { at: path });
          return;
        }
        if (rowspan && !rowSpan) {
          setNodes(editor, { rowSpan: parseInt(rowspan, 10) }, { at: path });
          return;
        }
      }
    }
    return normalizeNode([node, path]);
  };

  return editor;
};
